// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState, useCallback } from 'react';

import {
  defaultVariantColorsResolver,
  type MantineColor,
  type MantineColorShade,
  type MantineTheme,
  type MantineThemeOverride,
  type MantineColorsTuple,
  type ModalProps,
  type VariantColorsResolver,
  Loader,
  LoadingOverlay,
  Badge,
  ActionIcon,
  alpha,
  // oxlint-disable-next-line no-restricted-imports
  Modal,
  Popover,
  DrawerRoot,
  Divider,
  Collapse,
  Card,
  Button,
  Tooltip,
} from '@mantine/core';

import { LogoLoader } from 'assets/icons/LogoLoader';

import actionIconClasses from './ActionIcon.module.css';

const customColorName = 'evolve';
const defaultPrimaryShade = 6;

const variantColorResolver: VariantColorsResolver = (input) => {
  if (input.variant === 'light-mantine') {
    return defaultVariantColorsResolver({
      ...input,
      variant: 'light',
    });
  }

  const defaultResolvedColors = defaultVariantColorsResolver(input);

  if (input.variant === 'light') {
    return {
      ...defaultResolvedColors,
      background: 'var(--mantine-color-white)',
      hover: 'var(--mantine-color-gray-0)',
      border: '1px solid var(--mantine-color-gray-4)',
      color: 'var(--mantine-color-gray-8)',
    };
  }

  if (input.variant === 'subtle') {
    const [baseColor = input.theme.primaryColor, shade = defaultPrimaryShade] = input.color?.split('.') ?? [];
    const color = `var(--mantine-color-${baseColor}-${shade})`;
    return {
      ...defaultResolvedColors,
      hover: alpha(color, 0.12),
      color,
    };
  }

  return defaultResolvedColors;
};

export const DEFAULT_TRANSITIONS_DURATION = 200;

const DEFAULT_MODAL_CONFIG = Modal.extend({
  vars: (_theme: MantineTheme, { size }: ModalProps) => ({
    root: {
      '--modal-size': size === 'xxl' ? 'max(80vw, var(--modal-size-xl))' : undefined,
    },
  }),
  defaultProps: {
    closeOnEscape: false,
    closeOnClickOutside: false,
    centered: true,
    // The default `activity` mode pauses a hidden modal's effects, so a registry modal hidden beneath another
    // would re-run them (refetching, rebuilding 3D previews) when revealed
    keepMountedMode: 'display-none',
    transitionProps: {
      duration: DEFAULT_TRANSITIONS_DURATION,
    },
  },
});

export const useGenerateMantineTheme = (enableCustomTheming = false): MantineThemeOverride => {
  const [customColors, setCustomColors] = useState<MantineColorsTuple>([
    '#E8F8FC',
    '#CFF2FC',
    '#95DDF3',
    '#6ED7F7',
    '#2FC6F4',
    '#00A8DB',
    '#007DA3',
    '#065E79',
    '#054B61',
    '#083745',
  ]);
  const [primaryShade, setPrimaryShade] = useState<MantineColorShade>(defaultPrimaryShade);

  const promptForCustomColors = useCallback(
    (e: KeyboardEvent) => {
      if (e.ctrlKey && e.code === 'KeyQ' && window.location.hostname !== 'foresite.evolvemep.com') {
        // oxlint-disable-next-line no-alert
        const newCustomColors = prompt('Enter new custom colors (10-tuple):', JSON.stringify(customColors));
        if (newCustomColors) setCustomColors(JSON.parse(newCustomColors) as MantineColorsTuple);
        // oxlint-disable-next-line no-alert
        const newPrimaryShade = prompt('Enter primary shade:', JSON.stringify(primaryShade));
        if (newPrimaryShade) setPrimaryShade(parseInt(newPrimaryShade, 10) as MantineColorShade);
      }
    },
    [customColors, primaryShade],
  );

  useEffect(() => {
    if (enableCustomTheming) {
      window.addEventListener('keyup', promptForCustomColors);
    }
    return () => window.removeEventListener('keyup', promptForCustomColors);
  }, [enableCustomTheming, promptForCustomColors]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--mantine-primary-color',
      `var(--mantine-color-${customColorName}-${primaryShade})`,
    );
  }, [primaryShade]);

  return {
    fontFamily: 'IBM Plex Sans',
    headings: {
      fontFamily: 'IBM Plex Sans',
      fontWeight: '600',
    },
    primaryColor: customColorName,
    primaryShade: {
      light: primaryShade,
      dark: 8,
    },
    colors: {
      'app-shell-gray': [
        '#eee',
        '#dbdbdb',
        '#cbcbcb',
        '#bbb',
        '#999',
        '#777879',
        '#4a4a4a',
        '#3a3a3a',
        '#2e2e2e',
        '#1D1E20',
      ],
      [customColorName]: customColors,
    },
    variantColorResolver,
    components: {
      ActionIcon: ActionIcon.extend({
        classNames: actionIconClasses,
        defaultProps: {
          variant: 'subtle',
        },
      }),
      Badge: Badge.extend({
        defaultProps: {
          tt: 'none',
          fw: 500,
          fz: 'xs',
        },
        vars: (_, props) => {
          if (props.size === 'xxl') {
            return {
              root: {
                '--badge-height': '40px',
                '--badge-padding-x': 'var(--mantine-spacing-md)',
                '--badge-fz': 'var(--mantine-font-size-sm)',
              },
            };
          }

          return {
            root: {
              '--badge-fz': 'var(--mantine-font-size-xs)',
            },
          };
        },
      }),
      Button: Button.extend({
        defaultProps: {
          fw: 500,
        },
      }),
      Card: Card.extend({
        defaultProps: {
          shadow: 'none',
          radius: 'md',
        },
      }),
      Collapse: Collapse.extend({
        defaultProps: {
          transitionTimingFunction: 'linear',
        },
      }),
      Divider: Divider.extend({
        defaultProps: {
          labelPosition: 'left',
        },
      }),
      Loader: Loader.extend({
        defaultProps: {
          loaders: { ...Loader.defaultLoaders, logo: LogoLoader },
          size: 'md',
        },
      }),
      LoadingOverlay: LoadingOverlay.extend({
        defaultProps: {
          overlayProps: { blur: 1 },
          loaderProps: { type: 'logo', size: 'xl' },
        },
      }),
      Modal: DEFAULT_MODAL_CONFIG,
      ModalRoot: DEFAULT_MODAL_CONFIG,
      Popover: Popover.extend({
        defaultProps: {
          shadow: 'md',
          transitionProps: {
            duration: DEFAULT_TRANSITIONS_DURATION,
          },
        },
      }),
      PopoverDropdown: Popover.Dropdown.extend({
        defaultProps: {
          maw: '95vw',
        },
      }),
      // Compound-API usage (`Drawer.Root`, `Drawer.Content`, ...) reads defaults keyed by each
      // part's own name (e.g. "DrawerRoot"), not the "Drawer" shorthand component.
      DrawerRoot: DrawerRoot.extend({
        defaultProps: {
          transitionProps: {
            duration: DEFAULT_TRANSITIONS_DURATION,
          },
        },
      }),
      Tooltip: Tooltip.extend({
        defaultProps: {
          events: { hover: true, focus: false, touch: true },
        },
      }),
    },
  };
};

/**
 * Converts a Mantine color (i.e. `red.6`)
 * to the proper CSS variable (i.e. `var(--mantine-color-red-6)`)
 */
export const convertMantineColorToCss = (color: MantineColor) => {
  const [baseColor, shade = defaultPrimaryShade] = color.split('.');
  return `var(--mantine-color-${baseColor}-${shade})`;
};
